// Composer Studio — 내장 데이터셋 표 위젯 칼럼 사본의 **로드 시 참조화 정규화** (P3, 2026-08-08)
//
// 배치된 table 위젯의 `fields[]`는 파생 시점 사본이라 데이터 탭에서 정본(DataDef.fields)을
// 고쳐도 배치본이 그대로였다(2026-08-07 결정의 병 자체). 렌더 해소는 rc `resolveTableFields`가
// **사본 우선**(`node.fields ?? 정본.fields ?? []`)이므로, 사본이 정본과 완전히 같은 위젯의
// 사본을 로드 시 지우면 그 위젯부터 정본 live 참조로 켜진다(opt-in 이행 — 화면 결과 불변).
// 다음 저장 때 DB도 함께 정리되는 migrateLegacyMergeFields/stripComputeDefFields와 같은
// "로드 정규화" 패턴.
//
// 지우지 않는 것(자동 제거 금지):
//  - 정본 없음(대응 DataDef 부재 — 카탈로그 스캐폴드 표 등): 사본이 유일한 칼럼 선언이다.
//  - 드리프트(정본과 다름): 어느 쪽이 맞는지 자동 판단 불가 — 위젯 칼럼 패널이 "정본과 다름"
//    배너 + [정본 채택] 수동 동선을 제공한다(tableFieldsMode가 그 판정 정본).
//
// 하강 규칙은 서버 collectSuggestionDictWidgets(report-composer.writeback.ts)와 동일:
// input + group children + formTable 고정 행 셀 위젯. (branch/modalTrigger/repeat 안의 표는
// 정규화를 건너뛴다 — 사본이 남을 뿐 동작은 동결 모드로 안전하고, 패널의 [정본 채택]으로
// 수동 참조화할 수 있다.)
//
// 결정: docs/decisions/2026-08-07-datadef-reference-model-design.md

import type {
  ComposerWidget,
  DataDef,
  DataDefField,
  InputWidget,
  ReportTemplate,
} from '@safetyhub/shared';
import { updateWidget } from '@safetyhub/report-composer';

/**
 * JSON 구조 deep-equal — 배열은 순서 포함 비교, 객체는 **키 순서 무시**(직렬화 순서 차이로
 * 같은 선언이 영영 드리프트 판정되는 것을 막는다). undefined 값 프로퍼티는 JSON에 실리지
 * 않는 것과 같게 "없음"으로 취급한다.
 */
function deepEqualJson(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqualJson(v, b[i]));
  }
  if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) return false;
  const ra = a as Record<string, unknown>;
  const rb = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ra), ...Object.keys(rb)]);
  for (const k of keys) if (!deepEqualJson(ra[k], rb[k])) return false;
  return true;
}

/** 사본↔정본 칼럼 선언 동일성 — 정규화·패널 배너가 같은 비교기를 공유한다. */
export function dataDefFieldsEqual(a: DataDefField[], b: DataDefField[]): boolean {
  return deepEqualJson(a, b);
}

/**
 * dataDef **전체** 동일성(fields·defaultRows·prefillFrom 등 선언 전부) — 붙여넣기 lockstep(P4)의
 * "같은 key + 내용 동일 → 기존 정본 재사용(리네임 면제)" 판정이 쓴다. 칼럼만 비교하는
 * dataDefFieldsEqual과 달리 defaultRows 등이 다르면 다른 데이터셋으로 본다(그때는 위젯 키
 * 재발급 + dataDef 동반 복제가 정답 — nodeClipboard 붙여넣기 참조).
 */
export function dataDefEqual(a: DataDef, b: DataDef): boolean {
  return deepEqualJson(a, b);
}

/**
 * 표 위젯 칼럼의 상태 판정 — 위젯 칼럼 패널(참조/동결 분기)과 정규화가 공유하는 정본.
 *  - reference: 사본 없음 + 정본 존재 → 정본 live 참조(선언 편집은 정본에 쓴다).
 *  - unbound: 사본 없음 + 정본 없음 → 칼럼 해소 불가(resolveTableFields=[]) — 바인딩 끊김.
 *  - frozen-no-def: 사본 있음 + 정본 없음 → 사본이 유일 선언(카탈로그 스캐폴드 표).
 *  - frozen-equal: 사본 있음 + 정본과 동일 → 정규화 대상(로드 walk 밖이었거나 세션 중 재일치).
 *  - frozen-drift: 사본 있음 + 정본과 다름 → 수동 판단([정본 채택]/사본 유지).
 */
export type TableFieldsMode =
  | { kind: 'reference'; def: DataDef }
  | { kind: 'unbound' }
  | { kind: 'frozen-no-def' }
  | { kind: 'frozen-equal'; def: DataDef }
  | { kind: 'frozen-drift'; def: DataDef };

export function tableFieldsMode(
  w: Pick<InputWidget, 'bindingKey' | 'fields'>,
  dataDefs?: DataDef[],
): TableFieldsMode {
  const def = dataDefs?.find((d) => d.key === w.bindingKey);
  if (!w.fields) return def ? { kind: 'reference', def } : { kind: 'unbound' };
  if (!def) return { kind: 'frozen-no-def' };
  return dataDefFieldsEqual(w.fields, def.fields)
    ? { kind: 'frozen-equal', def }
    : { kind: 'frozen-drift', def };
}

/** 배열을 요소 변환해 변경이 있을 때만 새 배열(없으면 원본 참조 — no-op 히스토리/렌더 방지). */
function mapKeepRef<T>(list: T[], f: (x: T) => T): T[] {
  let changed = false;
  const next = list.map((x) => {
    const m = f(x);
    if (m !== x) changed = true;
    return m;
  });
  return changed ? next : list;
}

export interface NormalizeDataDefFieldsResult {
  template: ReportTemplate;
  // 아래 두 진단 목록은 **현재 프로덕션 소비처가 없다** — 정규화가 무엇을 건드렸는지 spec이 단언하는
  // 용도이고, 향후 "정규화 N건/드리프트 N건" 배지를 붙일 때의 자리다. 호출부가 무시해도 정상.
  /** 사본==정본이라 사본을 지운(참조화) 위젯. */
  referenced: { widgetId: string; bindingKey: string }[];
  /** 정본과 달라 보존한 사본(패널 드리프트 배너는 tableFieldsMode로 따로 판정한다). */
  drifted: { widgetId: string; bindingKey: string }[];
}

/**
 * composerWidgetTree의 table 위젯 사본을 정본과 대조해 **동일하면 삭제**(참조화)한다.
 * 드리프트·정본 없음은 그대로 보존. 변경이 없으면 원본 참조를 그대로 반환한다.
 */
export function normalizeDataDefFieldRefs(t: ReportTemplate): NormalizeDataDefFieldsResult {
  const referenced: NormalizeDataDefFieldsResult['referenced'] = [];
  const drifted: NormalizeDataDefFieldsResult['drifted'] = [];
  const tree = t.composerWidgetTree;
  const defs = t.dataDefs;
  if (!tree || tree.length === 0 || !defs || defs.length === 0) {
    return { template: t, referenced, drifted };
  }

  const visitInput = (w: InputWidget): InputWidget => {
    if (w.widget !== 'table' || !w.fields) return w;
    const mode = tableFieldsMode(w, defs);
    if (mode.kind === 'frozen-drift') {
      drifted.push({ widgetId: w.id, bindingKey: w.bindingKey });
      return w;
    }
    if (mode.kind !== 'frozen-equal') return w; // frozen-no-def — 유일 선언 보존
    referenced.push({ widgetId: w.id, bindingKey: w.bindingKey });
    const next = { ...w };
    delete next.fields;
    return next;
  };
  const visit = (w: ComposerWidget): ComposerWidget => {
    if (w.kind === 'input') return visitInput(w);
    if (w.kind === 'group') {
      const children = mapKeepRef(w.children, visit);
      return children === w.children ? w : { ...w, children };
    }
    if (w.kind === 'formTable') {
      const rows = mapKeepRef(w.rows, (row) => {
        if (row.type !== 'fixed') return row;
        const cells = mapKeepRef(row.cells, (cell) => {
          if (!cell.widget) return cell;
          const nw = visitInput(cell.widget);
          return nw === cell.widget ? cell : { ...cell, widget: nw };
        });
        return cells === row.cells ? row : { ...row, cells };
      });
      return rows === w.rows ? w : { ...w, rows };
    }
    return w;
  };

  const mapped = mapKeepRef(tree, visit);
  return {
    template: mapped === tree ? t : { ...t, composerWidgetTree: mapped },
    referenced,
    drifted,
  };
}

/**
 * 그 데이터셋 key를 bindingKey로 쓰는 **배치된 table 위젯 수** — 데이터 탭 카드·참조 모드 패널의
 * "배치 N곳에 반영" 파급 안내용.
 *
 * ⚠️ **표시(안내) 전용**이라 컨테이너를 전부 하강한다(group·formTable 고정 셀 + modalTrigger 콘텐츠·
 * repeat 행 템플릿·branch 슬롯). 정규화 walk(`normalizeDataDefFieldRefs`)는 이보다 **좁게** 도는데,
 * 그건 자동 사본 삭제라 fail-safe로 의도한 축소다(파일 머리 주석이 근거) — 두 하강 규칙이 다른 것은
 * 드리프트가 아니다. 여기서까지 좁히면 분기 안 표를 고르고도 "배치 0곳"이라는 자기모순이 난다.
 */
export function countTablePlacements(tree: ComposerWidget[] | undefined, key: string): number {
  let n = 0;
  const check = (w: InputWidget): void => {
    if (w.widget === 'table' && w.bindingKey === key) n += 1;
  };
  const walk = (widgets: ComposerWidget[]): void => {
    for (const w of widgets) {
      if (w.kind === 'input') check(w);
      else if (w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat')
        walk(w.children);
      else if (w.kind === 'branch') for (const slot of w.branches) walk(slot.children);
      else if (w.kind === 'formTable') {
        // formTable의 repeat 행은 열↔필드 매핑(cellFields)뿐이라 중첩 위젯이 없다 — 고정 행만.
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) if (cell.widget) check(cell.widget);
        }
      }
    }
  };
  if (tree) walk(tree);
  return n;
}

/** detach 사본 key — `${base}-copy`, 충돌 시 `-copy2`… (dataDefs+dataSources 전 key 회피). */
export function nextDetachedDefKey(base: string, takenKeys: Iterable<string>): string {
  const taken = new Set(takenKeys);
  let key = `${base}-copy`;
  let n = 2;
  while (taken.has(key)) key = `${base}-copy${n++}`;
  return key;
}

/**
 * "이 표만 다르게" detach — 참조 중인 정본을 **복제**하고 이 위젯의 bindingKey만 새 key로
 * 전환한다(다른 배치·정본 무영향, 저장 키가 갈라져 스키마 불일치 없음 — Figma detach 대응).
 *
 * ⚠️ 호출 전 **참조 검사 통과가 전제**다: bindingKey는 저장 content 키이자 본문 바인딩·집계·
 * 계산 입력·visibleWhen 등이 참조할 수 있는 키라, 이 위젯 밖 참조가 있으면 호출부가 차단한다
 * (countDataSourceUsage 재사용 — 일괄 리네임은 시도하지 않는다). 이 함수는 순수 변환만 담당.
 *
 * 위젯이 트리에 없거나 정본이 없으면 원본을 그대로 반환한다(no-op).
 */
export function detachTableWidgetDataDef(
  t: ReportTemplate,
  widget: Pick<InputWidget, 'id' | 'bindingKey'>,
): { template: ReportTemplate; newKey: string } | null {
  const defs = t.dataDefs ?? [];
  const def = defs.find((d) => d.key === widget.bindingKey);
  if (!def || !t.composerWidgetTree) return null;
  const newKey = nextDetachedDefKey(widget.bindingKey, [
    ...defs.map((d) => d.key),
    ...(t.dataSources ?? []).map((s) => s.key),
  ]);
  const cloned: DataDef = {
    ...structuredClone(def),
    key: newKey,
    label: `${def.label || def.key} (사본)`,
  };
  const tree = updateWidget(t.composerWidgetTree, widget.id, (x) =>
    x.kind === 'input' ? { ...x, bindingKey: newKey } : x,
  );
  if (tree === t.composerWidgetTree) return null; // 위젯 미발견 — 트리 불변이면 detach 무효
  return {
    template: { ...t, dataDefs: [...defs, cloned], composerWidgetTree: tree },
    newKey,
  };
}
